import React, { useRef, useState } from "react";
import moment from "moment";
import { ChangePasswordComponent } from "../ChangePasswordComponent";
import { trackLogout } from "../../helpers/activityTracker";
import { getMySession } from "./userMenuService";
import "./TopUserSetup.css";

const NETWORK = {
  open: { text: "No network restriction", tone: "muted" },
  inside: { text: "Inside allowed network", tone: "ok" },
  outsideAllowed: { text: "Outside — allowed for you", tone: "warn" },
  systemUser: { text: "Outside — system user", tone: "muted" },
};

const session = (key) => (sessionStorage.getItem(`session_${key}`) ?? "").trim();
const timeText = (utc) => (utc ? moment(utc).format("DD/MM/YYYY HH:mm") : "");

/** "Asha Rao" → "AR"; falls back to the user name. */
export function initials(first, last, userName) {
  const text = `${(first || "").charAt(0)}${(last || "").charAt(0)}`.trim() || (userName || "?").slice(0, 2);
  return text.toUpperCase();
}

function Row({ icon, label, children }) {
  return (
    <li className="tus-row">
      <i className={`tus-row__icon ${icon}`} aria-hidden="true" />
      <span className="tus-row__label">{label}</span>
      <span className="tus-row__value">{children}</span>
    </li>
  );
}

/**
 * Top-right user menu: who is logged in (name, role, contact, airline), this session (started at, IP), the previous
 * login (to spot one the user does not recognise) and how this login passes the airline's allowed networks. The session
 * part loads once, when the menu is first opened. Change Password and Logout as before.
 */
export function TopUserSetupComponent() {
  const changePassword = useRef(null);
  const [info, setInfo] = useState(undefined); // undefined = not loaded yet, null = not available
  const [loading, setLoading] = useState(false);
  const first = session("firstName");
  const last = session("lastName");
  const userName = session("userName");
  const name = `${first} ${last}`.trim() || userName;

  const loadOnce = () => {
    if (info !== undefined || loading) return;
    setLoading(true);
    getMySession().then((s) => {
      setInfo(s);
      setLoading(false);
    });
  };

  const logout = (e) => {
    e.preventDefault();
    trackLogout();
    sessionStorage.clear();
    window.location.href = "/";
  };

  const openChangePassword = (e) => {
    e.preventDefault();
    if (changePassword.current) changePassword.current.showModal();
  };

  const network = info?.network ? NETWORK[info.network] : null;

  return (
    <>
      <ul className="navbar-nav navbar-right">
        <li className="dropdown mr-0 user-profile">
          <a href="#" data-toggle="dropdown" className="dropdown-toggle nav-link-user" aria-label="User menu" onClick={loadOnce}>
            <img alt="" src="./assets/img/avatar/avatar-1.png" className="rounded-circle mr-1" style={{ width: "20px" }} />
          </a>
          <div className="dropdown-menu dropdown-list dropdown-menu-right rounded-vlg tus-menu">
            <div className="tus-head">
              <span className="tus-avatar" aria-hidden="true">
                {initials(first, last, userName)}
                <i className="avatar-presence online" />
              </span>
              <div className="tus-head__text">
                <strong className="tus-name">{name}</strong>
                <span className="tus-user">@{userName}</span>
                {session("userTypeName") && <span className="tus-role">{session("userTypeName")}</span>}
              </div>
            </div>

            <ul className="tus-rows">
              <Row icon="fas fa-envelope" label="Email">
                <span className="tus-oneline" title={session("email") || ""}>
                  {session("email") || "-"}
                </span>
              </Row>
              <Row icon="fas fa-phone" label="Phone">
                {session("phoneNumber") || "-"}
              </Row>
              <Row icon="fas fa-plane" label="Airline">
                {[session("airlineCode"), session("icaoCode")].filter(Boolean).join(" · ") || "-"}
              </Row>
              {(loading || info) && (
                <>
                  <Row icon="fas fa-sign-in-alt" label="Logged in">
                    {loading ? "…" : timeText(info.sessionStartedAt) || "-"}
                  </Row>
                  <Row icon="fas fa-network-wired" label="This IP">
                    {loading ? "…" : info.currentIp || "-"}
                  </Row>
                  <Row icon="fas fa-history" label="Last login">
                    {loading ? "…" : info.lastLoginAt ? `${timeText(info.lastLoginAt)}${info.lastLoginIp ? ` · from ${info.lastLoginIp}` : ""}` : "First login"}
                  </Row>
                  <Row icon="fas fa-shield-alt" label="Network">
                    {loading ? "…" : network ? <span className={`tus-pill tus-pill--${network.tone}`}>{network.text}</span> : "-"}
                  </Row>
                </>
              )}
            </ul>

            <a href="#" className="dropdown-item has-icon" onClick={openChangePassword}>
              <i className="fas fa-lock-open" /> Change Password
            </a>
            <a href="#" onClick={logout} className="dropdown-item has-icon mb-2">
              <i className="fas fa-sign-out-alt" /> Logout
            </a>
          </div>
        </li>
      </ul>
      <ChangePasswordComponent ref={changePassword} />
    </>
  );
}
